import { useEffect, useState } from "react";
import { Button, Card, Col, ColorPicker, Form, Input, Row, Switch, Tabs, Upload, message } from "antd";
import { DeleteOutlined, UploadOutlined } from "@ant-design/icons";
import { t } from "i18next";
import SimpleLayout from "../../core/components/layout/SimpleLayout";
import AppConfigTab from "./AppConfigTab";
import { PERMISSIONS, useUser } from "../../core/context/UserContext";
import ApiService from "../../core/service/ApiService";
import { loadBranding } from "../../core/service/BrandingService";
import "./branding.css";

const { TextArea } = Input;

/**
 * Beyaz etiket marka yönetimi.
 *
 * Görseller DB'de tutulur; sunucuya dosya kopyalamak, deploy'a eklemek yok.
 * Bir görsel silinince uygulama o alan için bizim gömülü varsayılanımıza döner (kısmi beyaz etiket).
 *
 * SVG kabul edilmez: SVG bir XML belgesidir, içine script gömülebilir ve aynı origin'den servis
 * edilince XSS'e dönüşür. Sunucu içerik tipini uzantıdan değil magic byte'tan doğrular.
 */
const ASSETS = [
    {
        kind: "HeaderLogo",
        title: t('Header logo'),
        hint: t('Sits on the header colour — use a variant that contrasts with it.'),
        preview: "header",
        bgField: "headerLogoBackground",
    },
    {
        kind: "LoginLogo",
        title: t('Login screen logo'),
        hint: t('Sits inside the login card, on a white background.'),
        preview: "light",
        bgField: "loginLogoBackground",
    },
    {
        kind: "LoginBackground",
        title: t('Login screen background'),
        hint: t('A photo. A dark overlay is applied on top for readability.'),
        preview: "light",
    },
    {
        kind: "Favicon",
        title: "Favicon",
        hint: t('Square PNG (64×64 or 192×192 recommended).'),
        preview: "light",
    },
];

/** antd ColorPicker değeri → "#rrggbb" | null (boş = renk yok) */
const toHex = (value) => {
    if (!value) return null;
    if (typeof value === "string") return value;
    return typeof value.toHexString === "function" ? value.toHexString() : null;
};

export default function BrandingPage()
{
    const { hasPermission } = useUser();
    const [form] = Form.useForm();
    const [supportForm] = Form.useForm();
    // Kaydet ucu tüm alanları birlikte yazar; iki sekme ayrı form olduğu için her kayıt
    // son yüklenen ayarların ÜZERİNE yazılır — biri diğerinin alanlarını boşaltmasın.
    const [settings, setSettings] = useState({});
    const [assets, setAssets] = useState({});
    const [colors, setColors] = useState({});
    const [loading, setLoading] = useState(false);

    // Uygulama ayarları (appsettings.json → App). Şemadan üretilir; ayrı izin ister.
    const canManageConfig = hasPermission(PERMISSIONS.CONFIG_MANAGE);
    const [configNodes, setConfigNodes] = useState([]);

    const loadConfig = async () => {
        if (!canManageConfig) return;
        const res = await ApiService.AppConfig.GetSchema();
        if (res?.success) setConfigNodes(res.data || []);
    };

    const load = async () => {
        const res = await ApiService.Branding.GetSettings();
        if (!res?.success) return;

        form.setFieldsValue({ ...res.data });
        supportForm.setFieldsValue({
            supportWhatsApp: res.data.supportWhatsApp,
            supportCategories: res.data.supportCategories,
        });
        setSettings(res.data);
        setAssets(res.data.assets || {});
        setColors({
            headerColor: res.data.headerColor || null,
            headerLogoBackground: res.data.headerLogoBackground || null,
            loginLogoBackground: res.data.loginLogoBackground || null,
        });
    };

    useEffect(() => {
        load();
        loadConfig();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const save = async (values) => {
        setLoading(true);
        const res = await ApiService.Branding.SaveSettings({
            ...settings,
            ...values,
            headerColor: toHex(values.headerColor),
            headerLogoBackground: toHex(values.headerLogoBackground),
            loginLogoBackground: toHex(values.loginLogoBackground),
        });
        setLoading(false);
        if (res?.success) {
            await load();
            await loadBranding();   // başlık/favicon/logo anında güncellensin
        }
    };

    const saveSupport = async (values) => {
        setLoading(true);
        const res = await ApiService.Branding.SaveSettings({ ...settings, ...values });
        setLoading(false);
        if (res?.success) {
            await load();
            await loadBranding();
        }
    };

    const upload = async (kind, file) => {
        const res = await ApiService.Branding.UploadAsset(kind, file);
        if (res?.success) {
            await load();
            await loadBranding();
        }
        return false;   // antd Upload'un kendi isteğini engelle
    };

    const remove = async (kind) => {
        const res = await ApiService.Branding.RemoveAsset(kind);
        if (res?.success) {
            await load();
            await loadBranding();
            message.info(t("Default branding restored for this asset"));
        }
    };

    const assetUrl = (kind) => {
        const key = kind.charAt(0).toLowerCase() + kind.slice(1);   // HeaderLogo → headerLogo
        const url = assets[key];
        return url ? `${window.AppSettings.server.api}${url}` : null;
    };

    /** Önizleme gerçek yüzeyi taklit eder: header kiracının renginde, giriş kartı beyaz. */
    const previewSurface = (asset) => {
        if (asset.preview !== "header") return undefined;
        return { background: colors.headerColor || "#34495e" };
    };
    const previewBox = (asset) => {
        const bg = asset.bgField ? colors[asset.bgField] : null;
        return bg ? { background: bg } : undefined;
    };

    // Marka sekmesinin içeriği (mevcut form) — sekme yapısına taşındı.
    const brandingTab = (
        <>
                <p className="branding-intro">
                    {t("Logos and titles are stored in the database. Uploading is enough — no file deployment needed. Removing an asset restores the built-in default.")}
                </p>

                <Form form={form} layout="vertical" onFinish={save}>
                    <Row gutter={[16, 16]}>
                        {ASSETS.map((asset) => (
                            <Col xs={24} md={12} key={asset.kind}>
                                <div className="branding-asset">
                                    <div className="branding-asset-head">
                                        <b>{t(asset.title)}</b>
                                        {assetUrl(asset.kind) && (
                                            <Button
                                                size="small"
                                                type="text"
                                                danger
                                                icon={<DeleteOutlined />}
                                                onClick={() => remove(asset.kind)}
                                            >
                                                {t("Remove")}
                                            </Button>
                                        )}
                                    </div>

                                    <div className="branding-preview" style={previewSurface(asset)}>
                                        {assetUrl(asset.kind)
                                            ? (
                                                <span className="branding-preview-box" style={previewBox(asset)}>
                                                    <img src={assetUrl(asset.kind)} alt="" />
                                                </span>
                                            )
                                            : <span className="branding-preview-empty">{t("Using default (Kuantum)")}</span>}
                                    </div>

                                    <div className="branding-asset-foot">
                                        <span className="branding-hint">{t(asset.hint)}</span>
                                        <Upload
                                            accept="image/png,image/webp,image/jpeg"
                                            showUploadList={false}
                                            beforeUpload={(file) => upload(asset.kind, file)}
                                        >
                                            <Button icon={<UploadOutlined />}>{t("Upload")}</Button>
                                        </Upload>
                                    </div>

                                    {/* zemin rengi: yalnız logolarda. Şeffaf olmayan logo, seçilen rengin kutusuna alınır. */}
                                    {asset.bgField && (
                                        <Form.Item
                                            name={asset.bgField}
                                            label={t("Logo background (leave empty if transparent)")}
                                            className="branding-color"
                                        >
                                            <ColorPicker
                                                allowClear
                                                format="hex"
                                                disabledAlpha
                                                onChange={(value) => setColors((p) => ({ ...p, [asset.bgField]: toHex(value) }))}
                                                onClear={() => setColors((p) => ({ ...p, [asset.bgField]: null }))}
                                            />
                                        </Form.Item>
                                    )}
                                </div>
                            </Col>
                        ))}
                    </Row>

                    <div className="branding-form">
                        <Row gutter={16}>
                            <Col xs={24} md={12}>
                                <Form.Item label={t("Institution name")} name="displayName">
                                    <Input placeholder={t("Shown in the header/footer")} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label={t("Header color")}
                                    name="headerColor"
                                    extra={t("Text color is computed automatically for contrast.")}
                                >
                                    <ColorPicker
                                        allowClear
                                        format="hex"
                                        disabledAlpha
                                        onChange={(value) => setColors((p) => ({ ...p, headerColor: toHex(value) }))}
                                        onClear={() => setColors((p) => ({ ...p, headerColor: null }))}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <h4 className="branding-section">{t("HIS application")}</h4>
                        <Row gutter={16}>
                            <Col xs={24} md={12}>
                                <Form.Item label={`${t("Browser title")} (TR)`} name="titleTr"><Input /></Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item label={`${t("Browser title")} (EN)`} name="titleEn"><Input /></Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item label={`${t("Description")} (TR)`} name="descriptionTr"><Input /></Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item label={`${t("Description")} (EN)`} name="descriptionEn"><Input /></Form.Item>
                            </Col>
                        </Row>

                        <h4 className="branding-section">{t("Patient portal (online results)")}</h4>
                        <Row gutter={16}>
                            <Col xs={24} md={12}>
                                <Form.Item label={`${t("Browser title")} (TR)`} name="onlineTitleTr"><Input /></Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item label={`${t("Browser title")} (EN)`} name="onlineTitleEn"><Input /></Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item label={`${t("Description")} (TR)`} name="onlineDescriptionTr"><Input /></Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item label={`${t("Description")} (EN)`} name="onlineDescriptionEn"><Input /></Form.Item>
                            </Col>

                            {/* Portalda satır satır gösterilir; her satır ayrı bir satırdır.
                                İletişimde BOŞ SATIR bırakmak şube/blok ayracı olarak kullanılır. */}
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label={`${t("Working hours")} (TR)`}
                                    name="onlineHoursTr"
                                    extra={t("One line per row.")}
                                >
                                    <TextArea rows={4} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item label={`${t("Working hours")} (EN)`} name="onlineHoursEn">
                                    <TextArea rows={4} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label={`${t("Contact")} (TR)`}
                                    name="onlineContactTr"
                                    extra={t("Leave an empty line to separate blocks.")}
                                >
                                    <TextArea rows={8} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item label={`${t("Contact")} (EN)`} name="onlineContactEn">
                                    <TextArea rows={8} />
                                </Form.Item>
                            </Col>
                        </Row>

                        <h4 className="branding-section">{t("Features")}</h4>
                        <Row gutter={16}>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label={t("POS payment")}
                                    name="posEnable"
                                    valuePropName="checked"
                                    extra={t("Shows the POS option on the payment screen.")}
                                >
                                    <Switch />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={12}>
                                <Form.Item
                                    label={t("Patient note popup")}
                                    name="patientNotePopup"
                                    valuePropName="checked"
                                    extra={t("Shows the patient note as a popup when the record opens.")}
                                >
                                    <Switch />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Button type="primary" htmlType="submit" loading={loading}>
                            {t("Save")}
                        </Button>
                    </div>
                </Form>
        </>
    );

    // Sağ alttaki yüzen WhatsApp destek butonunun ayarları.
    const supportTab = (
        <Form form={supportForm} layout="vertical" onFinish={saveSupport} className="branding-form">
            <Row gutter={16}>
                <Col xs={24} md={12}>
                    <Form.Item
                        label={t("WhatsApp number")}
                        name="supportWhatsApp"
                        extra={t("With country code, e.g. 905xxxxxxxxx. Leave empty to hide the support button.")}
                    >
                        <Input placeholder="905xxxxxxxxx" />
                    </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                    <Form.Item
                        label={t("Categories")}
                        name="supportCategories"
                        extra={t("One category per row.")}
                    >
                        <TextArea rows={6} />
                    </Form.Item>
                </Col>
            </Row>

            <Button type="primary" htmlType="submit" loading={loading}>
                {t("Save")}
            </Button>
        </Form>
    );

    /**
     * Ayar sekmeleri ŞEMADAN üretilir: üst düzey obje düğümleri (Mail, Sms, HL7, Ris) kendi
     * sekmesini alır; kök seviyedeki tekil alanlar (Disk, ApiAddress, MfaEnabled…) "Genel" sekmesinde
     * toplanır. Yeni bir ayar eklendiğinde bu ekranda kod değişikliği gerekmez.
     */
    const configTabs = () => {
        if (!canManageConfig || configNodes.length === 0) return [];

        const scalars = configNodes.filter((n) => !n.children);
        const groups = configNodes.filter((n) => n.children?.length);

        const tabs = [];

        if (scalars.length) {
            tabs.push({
                key: 'general',
                label: t('General'),
                children: <AppConfigTab nodes={scalars} onSaved={loadConfig} />,
            });
        }

        groups.forEach((group) => {
            tabs.push({
                key: group.key,
                label: group.name,
                children: <AppConfigTab nodes={group.children} onSaved={loadConfig} />,
            });
        });

        return tabs;
    };

    return (
        <SimpleLayout fullWidth={true}>
            <Card className="branding-card">
                <Tabs
                    defaultActiveKey="branding"
                    items={[
                        { key: 'branding', label: t('Branding'), children: brandingTab },
                        { key: 'whatsapp', label: 'WhatsApp', children: supportTab },
                        ...configTabs(),
                    ]}
                />
            </Card>
        </SimpleLayout>
    );
}
